import { strings } from '@/shared/strings';
import styles from './BarList.module.css';

export interface BarRow {
  label: string;
  value: number;
  /** What to print at the end of the row. Defaults to the value itself. */
  display?: string;
}

export interface BarListProps {
  rows: BarRow[];
  tone?: 'gold' | 'soft' | 'ink';
}

/**
 * A horizontal bar list. Section 2: no chart library.
 *
 * A library would be the single largest dependency in the bundle for four
 * simple visualisations, and this is a div with a width.
 *
 * Bars are scaled against the largest value rather than a fixed maximum, so a
 * ranking reads as a ranking. The floor is 1 so a set of zeroes does not divide
 * by nothing.
 */
export function BarList({ rows, tone = 'gold' }: BarListProps) {
  if (rows.length === 0) {
    return <p className={styles.empty}>{strings.reports.noData}</p>;
  }

  const max = Math.max(...rows.map((row) => row.value), 1);
  const fill = tone === 'soft' ? styles.soft : tone === 'ink' ? styles.ink : '';

  return (
    <div>
      {rows.map((row) => (
        <div key={row.label} className={styles.row}>
          <span className={styles.label} title={row.label}>
            {row.label}
          </span>
          <span className={styles.track}>
            <i
              className={`${styles.fill} ${fill}`}
              style={{ width: `${Math.round((row.value / max) * 100)}%` }}
            />
          </span>
          <span className={styles.value}>{row.display ?? row.value}</span>
        </div>
      ))}
    </div>
  );
}
